import React, {Fragment, useEffect} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledButtonDropdown,
} from 'reactstrap';
import styled from 'styled-components';

import {RichTextarea} from 'app/components/general/RichTextarea';
import {cats, withShortcuts} from 'app/general/shortcuts/withShortcuts';
import {
  ProcedureStepStructure,
  StyledStepContainerDIV,
} from 'app/procedures/components/ProcedureStepStructure';
import {ProcedureStepTitleInput} from 'app/procedures/components/ProcedureStepTitle';
import {InlineExclusiveGatewayIcon} from 'app/procedures/components/step-types-icons';
import {StepChoicesShaper} from 'app/steps/document-shaper';
import {gt} from 'app/utils/i18n';
import {cdnImageSrc} from 'app/utils/strings';

import {EditableProcedureAutomationFooter} from './EditableProcedureAutomationFooter';
import {EditableProcedureDecisionFooter} from './EditableProcedureDecisionFooter';
import {EditableProcedureFooter} from './EditableProcedureFooter';

import {AddNewStepForm} from '../AddNewStepForm';

import type {WithShortcutsProps} from 'app/general/shortcuts/withShortcuts';
import type {AttachedChoiceType} from 'app/general/types';
import type {
  ApplyStepShaperFuncType,
  DocumentShaper,
} from 'app/steps/document-shaper';
import type {
  ProcedureStepType,
  ProcedureVersionSerializedType,
} from 'sweet-types';
import type {AutomationType} from 'sweet-types/procedure/procedure-step';

type OwnProps = {
  step: ProcedureStepType;
  steps: Array<ProcedureStepType>;
  stepLaneId?: string | null;
  isBusy: boolean;
  onDisableEditing: () => void;
  applyStepShaper: ApplyStepShaperFuncType;
  onRemoveStep: (id: string) => void;
  onStepDataUpdate: (
    stepData: {
      title?: string;
      content?: string;
      automation?: AutomationType;
      next?: string;
      choices?: Array<AttachedChoiceType>;
    },
    id: string
  ) => void;
  onFinishEditing: (id: string) => void;
  onChangeBusyStatus: (id: string, busy: boolean) => void;
  addNewStep: (step: {
    type?: 'content' | 'decision' | 'ending' | 'processprocedure';
    after?: string;
  }) => void;
  onToggleReorderMode: () => void;
  mediaURLRead: string;
  url: string;
  contentType: 'procedureversion' | 'processversion';
  richTextContentType: 'procedureversion' | 'proceduregroup';
  richTextObjectId: number;
  onMount: () => unknown;
  noBottomBorder?: boolean;
  onAddProcedure?: (version: ProcedureVersionSerializedType) => unknown;
};

type Props = OwnProps & WithShortcutsProps;

const ActiveEditableProcedureStep = ({
  step,
  url,
  mediaURLRead,
  isBusy,
  steps,
  applyStepShaper,
  contentType,
  richTextContentType,
  richTextObjectId,
  addNewStep,
  onToggleReorderMode,
  onMount,
  stepLaneId,
  noBottomBorder,
  enableShortcut,
  onStepDataUpdate,
  onChangeBusyStatus,
  onFinishEditing,
  onAddProcedure,
  onRemoveStep,
}: Props) => {
  useEffect(() => {
    enableShortcut('esc', (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target != null && target.blur != null) {
        target.blur();
      }

      setTimeout(() => handleFinishEditing(), 100);
    });
    return () => handleEditorIsBusyChange(false);
  }, []);

  const updateProcedureTitle = (title: string) =>
    onStepDataUpdate(
      {
        title,
      },
      step.id
    );

  const updateProcedureAutomation = (automation: AutomationType) =>
    onStepDataUpdate(
      {
        automation,
      },
      step.id
    );

  const handleTitleInputKeyPress = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (event.key === ' ') {
      updateProcedureTitle(event.currentTarget.value);
    }
  };

  const handleTitleInputBlur = (
    event: React.SyntheticEvent<HTMLTextAreaElement>
  ) => {
    const target = event.target as HTMLInputElement;
    if (target.value !== step.title) {
      updateProcedureTitle(target.value);
    }
  };
  const handleEditorIsBusyChange = (isBusy: boolean) => {
    onChangeBusyStatus(step.id, isBusy);
  };

  const handleContentChange = (content: string) => {
    const cdnContent = cdnImageSrc(step.content, mediaURLRead, 'original');
    // remove the fr-draggable class from comparison since this exists just in editor.
    const cleanDraggable = content.replace(/ fr-draggable"/g, '"');
    if (cleanDraggable !== cdnContent) {
      onStepDataUpdate(
        {
          content,
        },
        step.id
      );
    }
  };
  const handleFinishEditing = () => {
    if (isBusy !== true) {
      onFinishEditing(step.id);
    }
  };

  const applyStepChoicesShaper = (
    func: (
      shaper: StepChoicesShaper,
      setEdit: (name: string | false) => any
    ) => DocumentShaper,
    immediate?: boolean
  ) =>
    applyStepShaper(
      (stepShaper, setEdit) => func(stepShaper.choices, setEdit),
      immediate
    );
  return (
    <StyledStepWrapperDiv noBottomBorder={noBottomBorder}>
      <StyledEditingStepDiv>
        <ProcedureStepStructure
          n={step.n}
          type={step.type}
          title={
            <ProcedureStepTitleInput
              defaultValue={step.title}
              onBlur={handleTitleInputBlur}
              onKeyPress={handleTitleInputKeyPress}
              placeholder={
                step.type === 'decision'
                  ? gt('Give this decision a title')
                  : gt('Give this step a title')
              }
            />
          }
          afterTitleContent={
            <div>
              <RichTextarea
                options={
                  step.type === 'ending'
                    ? {
                        heightMin: 80,
                      }
                    : undefined
                }
                onEditorIsBusyChange={handleEditorIsBusyChange}
                value={cdnImageSrc(step.content, mediaURLRead, 'original')}
                onChange={handleContentChange}
                contentType={richTextContentType}
                objectId={richTextObjectId}
                onCloseRequest={handleFinishEditing}
                onInitialized={onMount}
              />
              {step.type === 'decision' ? (
                <EditableProcedureDecisionFooter
                  url={url}
                  step={step}
                  choices={step.choices || []}
                  steps={steps}
                  applyStepChoicesShaper={applyStepChoicesShaper}
                  isProcess={contentType === 'processversion'}
                  applyStepShaper={applyStepShaper}
                  stepLaneId={stepLaneId}
                  onAddProcedure={onAddProcedure}
                />
              ) : step.type === 'automation' ? (
                <EditableProcedureAutomationFooter
                  step={step}
                  automation={step.automation}
                  onChange={updateProcedureAutomation}
                  applyStepShaper={applyStepShaper}
                  stepLaneId={stepLaneId}
                />
              ) : step.type === 'content' ||
                step.type == null ||
                step.type === 'ending' ? (
                <EditableProcedureFooter
                  step={step}
                  steps={steps}
                  stepLaneId={stepLaneId}
                  applyStepShaper={applyStepShaper}
                  onBusy={(id, isBusy) => handleEditorIsBusyChange(isBusy)}
                />
              ) : null}

              <div className="bottom-actions clearfix">
                <button
                  onClick={() => onRemoveStep(step.id)}
                  className="btn btn-link text-white"
                >
                  <i className="fa fa-times" /> {gt('Delete Step')}
                </button>

                <div className="float-right">
                  <UncontrolledButtonDropdown>
                    <DropdownToggle
                      className="mb-1 mb-sm-0 ml-1"
                      color="light"
                      disabled={isBusy}
                      caret
                    >
                      {gt('Actions')}
                    </DropdownToggle>

                    <DropdownMenu>
                      {step.type !== 'decision' ||
                      typeof step.type === 'undefined' ? (
                        <Fragment>
                          <DropdownItem
                            onClick={() =>
                              applyStepShaper((stepShaper) =>
                                stepShaper.convertToDecisionType().procedure()
                              )
                            }
                          >
                            <InlineExclusiveGatewayIcon />{' '}
                            {gt('Convert to a decision')}
                          </DropdownItem>
                        </Fragment>
                      ) : null}

                      {contentType === 'procedureversion' &&
                      step.type !== 'content' ? (
                        <DropdownItem
                          onClick={() =>
                            applyStepShaper((stepShaper) =>
                              stepShaper.convertToContentType().procedure()
                            )
                          }
                        >
                          <i className="fas fa-dot-circle" />{' '}
                          {gt('Convert to a normal step')}
                        </DropdownItem>
                      ) : null}

                      {step.type !== 'ending' ? (
                        <DropdownItem
                          onClick={() =>
                            applyStepShaper((stepShaper) =>
                              stepShaper.convertToEndingType().procedure()
                            )
                          }
                        >
                          <i className="far fa-circle" />{' '}
                          {gt('Convert to an ending')}
                        </DropdownItem>
                      ) : null}

                      <DropdownItem
                        onClick={() =>
                          applyStepShaper((stepShaper, setEdit) =>
                            stepShaper
                              .clone()
                              .runSideEffect((clonedStepShaper) =>
                                setEdit(clonedStepShaper.getId())
                              )
                              .procedure()
                          )
                        }
                      >
                        <i className="fa fa-fw fa-copy" /> {gt('Duplicate')}
                      </DropdownItem>
                      <DropdownItem onClick={onToggleReorderMode}>
                        <i className="fa fa-sort" /> {gt('Move steps')}
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledButtonDropdown>

                  {isBusy ? (
                    <button
                      className="btn btn-primary ml-1 d-block d-sm-inline-block"
                      disabled
                    >
                      <i className="fa fa-circle-notch fa-spin" />{' '}
                      {gt('Saving...')}
                    </button>
                  ) : (
                    <button
                      onClick={handleFinishEditing}
                      className="btn btn-primary ml-1 d-block d-sm-inline-block"
                    >
                      <i className="fa fa-thumbs-up" /> {gt('Finished Editing')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          }
          noBottomBorder={noBottomBorder}
        />
      </StyledEditingStepDiv>

      {contentType !== 'processversion' && step.n !== steps.length ? (
        <AddNewStepForm
          title={gt('Add a step here...')}
          addNewStep={(type) =>
            addNewStep({
              after: step.id,
              type,
            })
          }
        />
      ) : null}
    </StyledStepWrapperDiv>
  );
};

export default withShortcuts<Props>({
  key: 'esc',
  name: () => '',

  /* the real name is defined in LoadableProcessEditPage */
  category: cats.editPage,
})(ActiveEditableProcedureStep);

export const StyledStepWrapperDiv = styled.div<{noBottomBorder?: boolean}>`
  ${({noBottomBorder}) =>
    noBottomBorder ? '' : 'border-bottom: 1px dotted ${colors.mediumGray};'}
  padding-bottom: 1rem;
`;
export const StyledEditingStepDiv = styled.div`
  background: ${colors.paleBlue};
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.4);
  border: 2px solid ${colors.mediumGreen};
  padding: 0.5rem;
  border-radius: 5px;
  margin-bottom: 1rem;

  .step-n {
    left: -2em !important;
    top: 0.125em;

    &.moved-from {
      left: -4.5rem !imporant;
    }
  }

  .bottom-actions {
    border-top: 2px solid ${colors.mediumGreen};
    background-color: ${colors.mediumGreen};
    padding: 0.5rem;
    margin: 0 -0.5rem -0.5rem;
  }

  ${StyledStepContainerDIV} {
    margin-bottom: 0;
    border-bottom: none;

    &::before {
      left: -3.1rem;
      background: ${colors.mediumGreen};
    }
  }
`;
